import { useState, type ReactElement } from "react";
import { HUD, SPACING, type FeedbackKind } from "odyssey-shared";
import { Button, Checkbox, ModalWindow, Segmented, TextArea, TextField, logical, type WindowBox } from "../../ui/kit/index.ts";
import { windowPlace, windowSize } from "../windows/places.ts";
import { say } from "../../words.ts";
import type { FeedbackDraft, SendOutcome } from "./send.ts";
import { FEEDBACK_FIELD_CHARS } from "./words.ts";

/**
 * The Feedback form (ADR-081's amendment of 2026-10-05, items 4 and 5): the kit's modal window, so a system window,
 * centred behind its scrim until it is moved or resized (P1.9-T184: from 220 by 280 to 420 by 520, kept per device under
 * `feedback`), opened over the game menu and closing back to it by its close button, Cancel
 * or Escape. A player's is titled "Report a bug" and sends a bug report; a staff member's is titled "Feedback" and
 * chooses the kind. It holds a title, a description, the sentence saying what is sent, a tick for the game's log
 * (on) and one for a screenshot (off for a player, since it shows other players' names and chat; on for staff),
 * then the outcome: the saved report's reference, or the refusal's sentence. Everything typed and everything the
 * server answered is drawn as text. The component imports no stylesheet; `menu/mount.tsx` imports the sheet.
 */

/**
 * The window's size in logical pixels on a page with room for it, and the gaps between its rows.
 */
export const FEEDBACK_WINDOW = { width: 260, height: 300, gap: SPACING[3], lines: 4 } as const;

/**
 * The window's box in an area: its own size or what the area leaves inside the HUD margin, in the middle.
 *
 * @param area The scrim's size in logical pixels, as measured; a fraction of a pixel is dropped.
 * @returns The box in whole logical pixels.
 * @example
 * feedbackWindowBox({ width: 640, height: 360 });
 * // { left: 190, top: 30, width: 260, height: 300 }
 */
export function feedbackWindowBox(area: { readonly width: number; readonly height: number }): WindowBox {
  const across = Math.floor(area.width);
  const down = Math.floor(area.height);
  const width = Math.max(0, Math.min(FEEDBACK_WINDOW.width, across - 2 * HUD.margin));
  const height = Math.max(0, Math.min(FEEDBACK_WINDOW.height, down - 2 * HUD.margin));
  return { left: Math.max(HUD.margin, Math.floor((across - width) / 2)), top: Math.max(HUD.margin, Math.floor((down - height) / 2)), width, height };
}

/**
 * Where a send stands: nothing sent, sending, saved under a reference, or refused with a sentence.
 */
type Sending = { readonly phase: "idle" | "sending" } | { readonly phase: "saved"; readonly reference: string } | { readonly phase: "refused"; readonly message: string };

/**
 * The Feedback form.
 *
 * @param props `staff`, whether the account may send both kinds; `onSend`, which sends a draft and never rejects;
 *   and `onBack`, called when the form leaves for the menu.
 * @returns The window.
 * @throws Error when mounted outside a `KitInputProvider`.
 * @example
 * <FeedbackWindow staff={false} onSend={(draft) => page.send(draft)} onBack={() => onEvent({ type: "backed" })} />
 */
export function FeedbackWindow({ staff, onSend, onBack }: { readonly staff: boolean; readonly onSend: (draft: FeedbackDraft) => Promise<SendOutcome>; readonly onBack: () => void }): ReactElement {
  const [kind, setKind] = useState<FeedbackKind>("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [withLog, setWithLog] = useState(true);
  const [withScreenshot, setWithScreenshot] = useState(staff);
  const [sending, setSending] = useState<Sending>({ phase: "idle" });
  const busy = sending.phase === "sending";
  const limits = staff ? FEEDBACK_FIELD_CHARS.staff : FEEDBACK_FIELD_CHARS.player;

  /**
   * Sends what the form holds and shows what came of it; a saved report empties the title and the description.
   */
  const send = (): void => {
    if (busy || title.trim() === "") {
      return;
    }
    setSending({ phase: "sending" });
    void onSend({ kind: staff ? kind : "bug", title, description, withLog, withScreenshot }).then((outcome) => {
      if (outcome.ok) {
        setTitle("");
        setDescription("");
        setSending({ phase: "saved", reference: outcome.reference });
      } else {
        setSending({ phase: "refused", message: outcome.message });
      }
    });
  };

  const footer = (
    <>
      <Button onClick={onBack}>{sending.phase === "saved" ? say("feedback.close") : say("feedback.cancel")}</Button>
      <Button variant="primary" onClick={send} disabled={title.trim() === ""} busy={busy} busyLabel={say("feedback.sending")}>
        {say("feedback.send")}
      </Button>
    </>
  );
  return (
    <ModalWindow id="feedback" place={windowPlace("feedback")} size={windowSize("feedback")} title={staff ? say("feedback.titles.staff") : say("feedback.titles.player")} onClose={onBack} fit={feedbackWindowBox} footer={footer}>
      <div className="ot-game-feedback" data-game-menu="feedback" data-reporter={staff ? "staff" : "player"} style={{ gap: logical(FEEDBACK_WINDOW.gap) }}>
        {staff ? (
          <div data-feedback="kind">
            <Segmented
              label={say("feedback.kind")}
              value={kind}
              onChange={setKind}
              options={[
                { value: "bug", label: say("feedback.kinds.bug"), disabled: busy },
                { value: "feature", label: say("feedback.kinds.feature"), disabled: busy },
              ]}
            />
          </div>
        ) : null}
        <TextField label={say("feedback.title")} value={title} onChange={setTitle} maxLength={limits.title} counter disabled={busy} name="feedback-title" autoComplete="off" />
        <TextArea label={say("feedback.description")} value={description} onChange={setDescription} lines={FEEDBACK_WINDOW.lines} maxLength={limits.description} disabled={busy} name="feedback-description" />
        <p className="ot-game-feedback__note" data-feedback="sent">
          {say("feedback.sent")}
        </p>
        <Checkbox label={say("feedback.withLog")} checked={withLog} onChange={setWithLog} disabled={busy} />
        <Checkbox label={say("feedback.withScreenshot")} checked={withScreenshot} onChange={setWithScreenshot} disabled={busy} />
        <p className="ot-game-feedback__note" data-feedback="screenshot-note">
          {say("feedback.screenshotNote")}
        </p>
        <p className={sending.phase === "refused" ? "ot-game-feedback__result is-refused" : "ot-game-feedback__result"} data-feedback="result" data-phase={sending.phase} role="status" aria-live="polite">
          {sending.phase === "saved" ? say("feedback.saved", { reference: sending.reference }) : sending.phase === "refused" ? sending.message : null}
        </p>
      </div>
    </ModalWindow>
  );
}
